import {
  ArticleBullets,
  ArticleImage,
  ArticleLayout,
  ArticleParagraph,
  ArticleSection,
  QuickVerdict,
} from '../../../components/marketing/ArticleLayout';
import {
  ARTICLE_IMAGES,
  ARTICLE_SUMMARY_BY_SLUG,
} from '../../../lib/content/articles';

const article = ARTICLE_SUMMARY_BY_SLUG['what-if-i-retire-earlier'];

export default function WhatIfIRetireEarlierPage() {
  return (
    <ArticleLayout
      article={article}
      relatedSlugs={['tax-aware-fire-planning', 'planning-for-kids-college-and-family-life-events']}
    >
      <QuickVerdict
        title="Quick take"
        items={[
          'The useful planning question is often how to move the timeline, not just where the timeline currently sits.',
          'Per Diem is built to help households test tradeoffs and scenarios instead of accepting one fixed retirement story.',
          'Earlier retirement usually means understanding what would need to change now, not just dreaming about the future date.',
        ]}
      />

      <ArticleSection title="Retirement planning gets interesting when the date becomes negotiable">
        <ArticleParagraph>
          Many people first approach FIRE planning as a discovery exercise. They want to know when
          they could retire if they keep doing roughly what they are doing now. That is a useful
          starting point, but it is rarely where the best decisions happen.
        </ArticleParagraph>
        <ArticleParagraph>
          The more compelling question is usually: what if we wanted this sooner? That question
          turns the plan from a forecast into a design problem.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.mobileScenarios}
        alt="Per Diem mobile scenario planning screen"
        caption="A useful plan is not just a projected destination. It is a place to compare what would actually shift the destination."
        variant="phone"
      />

      <ArticleSection title="Scenario thinking makes the tradeoffs visible">
        <ArticleBullets
          items={[
            'What happens if savings increase meaningfully for the next few years?',
            'What happens if housing costs stay high longer than expected?',
            'What happens if one partner reduces work or leaves a job?',
            'What happens if a major future expense becomes more concrete?',
          ]}
        />
      </ArticleSection>

      <ArticleSection title="Why this is better than treating the plan as fixed">
        <ArticleParagraph>
          A static plan can be comforting, but it also makes the household passive. It suggests the
          future date is something to observe rather than something to shape.
        </ArticleParagraph>
        <ArticleParagraph>
          Per Diem is more useful when it helps the household understand the relationship between
          current behavior and future optionality. That is what makes the plan feel alive.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.mobileFirePlan}
        alt="Per Diem mobile FIRE plan screen"
        caption="The daily product experience matters because an earlier retirement date is built from present-day choices and visible tradeoffs."
        variant="phone"
      />

      <ArticleSection title="An earlier timeline is really a series of present-day choices">
        <ArticleParagraph>
          Retiring earlier is rarely about one dramatic move. It is usually the compounded result of
          many smaller choices around spending, income, liabilities, and family priorities. The tool
          becomes valuable when it can show those relationships clearly enough for the household to
          respond.
        </ArticleParagraph>
        <ArticleParagraph>
          That is the real value of scenario-friendly planning. It turns the question from fantasy
          into something the household can reason about.
        </ArticleParagraph>
      </ArticleSection>
    </ArticleLayout>
  );
}
